import { Component, type ReactNode, type ErrorInfo } from 'react';

import { captureException } from '../lib/sentry.js';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback customizado. Default: tela de erro padronizada com botão de reload. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary raiz do app — captura erros de render/lifecycle em qualquer
 * rota filha e mostra fallback amigável em vez de tela em branco. Envia o
 * stack para o Sentry via `captureException`.
 *
 * Use **uma vez** envolvendo o `<Routes>` raiz em `App.tsx`. Para boundaries
 * mais granulares (ex: widget de IA que pode falhar sem derrubar a tela),
 * crie instâncias locais.
 *
 * Nota: erros em event handlers e em effects assíncronos NÃO são capturados
 * automaticamente por ErrorBoundary — para esses, use try/catch + `captureException`.
 *
 * §5.3 — EXCEÇÃO às strings i18n: o fallback desta classe usa pt-BR hardcoded
 * intencionalmente. Se o i18n quebrar (I18nProvider com erro de carga), o
 * fallback ainda precisa renderizar. Um `useT()` aqui criaria loop infinito
 * de erro. Strings desta classe ficam travadas em pt-BR; se a plataforma um
 * dia precisar de outros idiomas, esta tela pode passar a usar um dicionário
 * estático baseado em `navigator.language` — sem react-i18next.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background p-6 text-center"
      >
        <h1 className="text-2xl font-semibold text-foreground">Algo deu errado</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          Tivemos um problema inesperado ao carregar esta tela. A equipe já foi notificada. Tente
          recarregar a página; se o problema persistir, entre em contato pelo suporte.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Recarregar
        </button>
      </div>
    );
  }
}
